import React from 'react';
import styled, { keyframes } from 'styled-components';
import Hero from '../components/Hero';
import { SliderDataSix } from '../data/SliderData';
import Packages from '../components/Packages';

/* ─── Animations ─────────────────────────────────────────────── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`

const scroll = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`

/* ─── Page ───────────────────────────────────────────────────── */
const PageWrapper = styled.div`
  background: #faf6f0;
  color: #2c1f14;
`

/* ─── Reviews Section ────────────────────────────────────────── */
const ReviewsSection = styled.section`
  padding: 90px 0 80px;
  background: #faf6f0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 50%; transform: translateX(-50%);
    width: 1px;
    height: 60px;
    background: linear-gradient(180deg, transparent, #b07d3a);
  }
`

const ReviewsHeader = styled.div`
  text-align: center;
  padding: 0 24px;
  margin-bottom: 56px;
  animation: ${fadeUp} 0.8s ease both;
`

const SectionEyebrow = styled.p`
  font-size: 0.65rem;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: #b07d3a;
  margin-bottom: 14px;
`

const SectionHeading = styled.h2`
  font-size: clamp(1.8rem, 3.5vw, 2.8rem);
  font-weight: 400;
  color: #2c1f14;
  margin: 0 auto 16px;
  line-height: 1.25;
`

const OrnamentDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 0 auto 0;
  max-width: 240px;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, #b07d3a);
  }
  &::after {
    background: linear-gradient(90deg, #b07d3a, transparent);
  }
`

const OrnamentDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #b07d3a;
  display: inline-block;
`

const GoogleBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;

  span {
    font-size: 0.78rem;
    letter-spacing: 0.12em;
    color: #7a5c44;
    text-transform: uppercase;
  }
`

/* ─── Marquee ────────────────────────────────────────────────── */
const MarqueeOuter = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 0; bottom: 0;
    width: 140px;
    z-index: 2;
    pointer-events: none;
  }
  &::before {
    left: 0;
    background: linear-gradient(90deg, #faf6f0, transparent);
  }
  &::after {
    right: 0;
    background: linear-gradient(90deg, transparent, #faf6f0);
  }
`

const MarqueeTrack = styled.div`
  display: flex;
  gap: 24px;
  width: max-content;
  animation: ${scroll} 40s linear infinite;
  padding: 20px 12px 28px;
  will-change: transform;

  &:hover {
    animation-play-state: paused;
  }
`

/* ─── Review Card ────────────────────────────────────────────── */
const ReviewCard = styled.div`
  background: #fff;
  border: 1px solid rgba(176, 125, 58, 0.15);
  border-radius: 4px;
  padding: 30px 28px 26px;
  width: 320px;
  flex-shrink: 0;
  position: relative;
  transition: box-shadow 0.35s, transform 0.35s;
  cursor: default;

  &:hover {
    box-shadow: 0 16px 48px rgba(44, 31, 20, 0.1);
    transform: translateY(-4px);
  }
`

const QuoteMark = styled.span`
  position: absolute;
  top: 12px;
  left: 20px;
  font-size: 4rem;
  line-height: 1;
  color: rgba(176, 125, 58, 0.09);
  pointer-events: none;
  user-select: none;
`

const GoogleIconCorner = styled.div`
  position: absolute;
  top: 18px;
  right: 18px;
  opacity: 0.2;
`

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`

const Avatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #b07d3a, #d4a057);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.88rem;
  font-weight: 600;
  flex-shrink: 0;
`

const AuthorName = styled.p`
  font-size: 0.88rem;
  font-weight: 600;
  color: #2c1f14;
  margin: 0 0 2px;
`

const ReviewDate = styled.p`
  font-size: 0.7rem;
  color: #a08060;
  margin: 0;
`

const Stars = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: 12px;
  color: #f4b942;
  font-size: 0.88rem;
`

const ReviewText = styled.p`
  font-size: 0.88rem;
  line-height: 1.8;
  color: #4a3728;
  font-style: italic;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

/* ─── CTA ────────────────────────────────────────────────────── */
const CTAWrapper = styled.div`
  text-align: center;
  margin-top: 52px;
  padding: 0 24px;
  animation: ${fadeUp} 0.8s 0.3s ease both;
`

const CTAEyebrow = styled.p`
  font-size: 0.62rem;
  letter-spacing: 0.38em;
  text-transform: uppercase;
  color: #b07d3a;
  margin-bottom: 16px;
`

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  text-decoration: none;
  padding: 20px 44px;
  border: 1px solid rgba(176, 125, 58, 0.35);
  background: #fff;
  color: #2c1f14;
  font-size: 0.95rem;
  letter-spacing: 0.05em;
  transition: background 0.35s, color 0.35s, border-color 0.35s, box-shadow 0.35s, transform 0.3s;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #b07d3a, transparent);
    opacity: 0;
    transition: opacity 0.35s;
  }

  &:hover {
    background: #2c1f14;
    color: #f5ede0;
    border-color: #2c1f14;
    box-shadow: 0 12px 40px rgba(44, 31, 20, 0.18);
    transform: translateY(-2px);
    &::before { opacity: 1; }
  }
`

const CTAArrow = styled.span`
  display: inline-block;
  color: #b07d3a;
  transition: transform 0.3s ease, color 0.35s;

  ${CTAButton}:hover & {
    transform: translateX(4px);
    color: #f5ede0;
  }
`

/* ─── Google SVG ─────────────────────────────────────────────── */
const GoogleSVG = ({ size = 22 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width={size} height={size}>
    <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.5 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.5 29.5 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z"/>
    <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.2l-6.3-5.3C29.4 35.1 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8H6.3C9.5 38.8 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.3 5.3C41.1 36.5 44 30.7 44 24c0-1.3-.1-2.6-.4-3.9z"/>
  </svg>
)

/* ─── Reviews Data ───────────────────────────────────────────── */
const reviews = [
  {
    name: "Lauren Ranallo",
    date: "October 2023",
    text: "Hidden gem!! Our expectations were blown away! The 'Love' room was luxurious with gorgeous views. Maria & Jonathan are incredible hosts. THE FOOD was amazing — I will be dreaming about her tortillas for months.",
  },
  {
    name: "Kathryn Eller",
    date: "August 2023",
    text: "All around a wonderful experience! It is a little slice of heaven on earth! Breakfast was phenomenal!! Both Jonathan and Maria are so kind and helpful! 10/10 recommend!!!!",
  },
  {
    name: "Mark Brooks",
    date: "September 2023",
    text: "We absolutely loved our weekend here. Comfortable rooms with a fireplace and beautiful views. Breakfast was delicious. We will definitely be back and recommend to everyone.",
  },
  {
    name: "Leslie",
    date: "July 2023",
    text: "I attended a baby shower here and the venue was just beautiful and the food was absolutely AMAZING!! Definitely planning a stay very soon.",
  },
  {
    name: "Maria G.",
    date: "June 2023",
    text: "The most magical anniversary stay. Every detail was so thoughtful — the cozy fireplace, handmade breakfast, the stunning mountain views from the porch. Truly our own private heaven.",
  },
  {
    name: "David & Sara T.",
    date: "May 2023",
    text: "From the moment we arrived we felt so at home. Maria's cooking is extraordinary — fresh and made with such love. Jonathan made sure we had everything we needed. Can't wait to return!",
  },
]

const allReviews = [...reviews, ...reviews]

/* ─── Component ──────────────────────────────────────────────── */
const Packagesscreen = () => {
  return (
    <PageWrapper>
      <Hero slides={SliderDataSix} />
      <Packages />

      <ReviewsSection>
        <ReviewsHeader>
          <GoogleBadge>
            <GoogleSVG size={22} />
            <span>Google Reviews</span>
          </GoogleBadge>
          <SectionEyebrow>What Our Guests Are Saying</SectionEyebrow>
          <SectionHeading>Little Heaven Bed &amp; Breakfast</SectionHeading>
          <OrnamentDivider><OrnamentDot /></OrnamentDivider>
        </ReviewsHeader>

        <MarqueeOuter>
          <MarqueeTrack>
            {allReviews.map((review, i) => (
              <ReviewCard key={i}>
                <QuoteMark>"</QuoteMark>
                <GoogleIconCorner><GoogleSVG size={18} /></GoogleIconCorner>
                <CardTop>
                  <Avatar>{review.name.charAt(0)}</Avatar>
                  <div>
                    <AuthorName>{review.name}</AuthorName>
                    <ReviewDate>{review.date}</ReviewDate>
                  </div>
                </CardTop>
                <Stars>★★★★★</Stars>
                <ReviewText>{review.text}</ReviewText>
              </ReviewCard>
            ))}
          </MarqueeTrack>
        </MarqueeOuter>

        <CTAWrapper>
          <CTAEyebrow>Enjoyed your stay?</CTAEyebrow>
          <CTAButton
           href="https://search.google.com/local/writereview?placeid=ChIJwfF2mIh9UYgRGeKE7QiWDxg"
            target="_blank"
            rel="noopener noreferrer"
          >
            <GoogleSVG size={20} />
            Leave Us a Google Review
            <CTAArrow>→</CTAArrow>
          </CTAButton>
        </CTAWrapper>
      </ReviewsSection>
    </PageWrapper>
  )
}

export default Packagesscreen